"use client";

import React from "react";
import { Shield, BookOpen, CircleCheck as CheckCircle, Users, ArrowRight } from 'lucide-react';
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
function EditorialTeamConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {[...Array(8)].map((_, i) => <Card key={i} data-index={i} className="animate-pulse overflow-hidden">
            <div className="h-48 bg-muted/50" />
            <CardHeader className="space-y-2">
              <div className="h-4 w-1/2 rounded bg-muted" />
              <div className="h-3 w-1/3 rounded bg-muted" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-3 w-full rounded bg-muted" />
                <div className="h-3 w-4/5 rounded bg-muted" />
              </div>
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-6 text-center text-destructive">
        <p>Gagal memuat data tim redaksi. Silakan coba lagi nanti.</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="rounded-lg border border-border bg-muted/20 p-12 text-center">
        <Users className="mx-auto mb-4 size-12 text-muted-foreground opacity-50" />
        <h3 className="font-default text-lg font-medium text-foreground">Belum ada data</h3>
        <p className="mt-2 text-sm text-muted-foreground">Data tim redaksi sedang dalam pembaruan.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {posts.map((post, i) => {
      const image = {
        src: post.customFields?.foto || post.featuredImage,
        srcset: "",
        sizes: ""
      };
      const name = post.customFields?.nama_lengkap || post.title;
      const role = post.customFields?.jabatan || "Redaksi";
      const initials = name.split(" ").map((n: string) => n[0]).join("").substring(0, 2).toUpperCase();
      return <Card key={post.id} data-index={i} className="flex h-full flex-col overflow-hidden transition-shadow hover:shadow-md">
            <div className="relative aspect-square w-full overflow-hidden bg-muted/30">
              {image.src ? <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={`Foto ${name}`} loading="lazy" className="size-full object-cover transition-transform duration-300 hover:scale-105" /> : <div className="flex size-full items-center justify-center bg-secondary/10">
                  <Users className="size-16 text-secondary/40" />
                </div>}
            </div>
            
            <CardHeader className="pb-4">
              <div className="mb-2 flex items-center justify-between">
                <Badge variant="secondary" className="font-medium">
                  {role}
                </Badge>
              </div>
              <CardTitle className="font-default text-xl line-clamp-1">{name}</CardTitle>
              {post.customFields?.wilayah && <CardDescription className="text-xs">
                  Biro: {post.customFields.wilayah}
                </CardDescription>}
            </CardHeader>
            
            <CardContent className="flex-grow pb-4">
              <p className="text-sm text-muted-foreground line-clamp-3">
                {post.customFields?.ringkasan_singkat || post.excerpt?.replace(/<[^>]*>?/gm, '') || "Anggota tim redaksi ABADI.NEWS yang berdedikasi menyajikan informasi terpercaya."}
              </p>
            </CardContent>
            
            <CardFooter className="pt-0">
              <Button asChild variant="outline" className="w-full group">
                <Link to={post.link || "#"}>
                  Lihat Profil
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function EditorialTeam() {
  const wp_query_params = {
    post_type: "pengurus",
    per_page: 12,
    orderby: "title",
    order: "asc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="editorial-team" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <div className="mx-auto max-w-3xl text-center mb-16">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
            Tentang Kami
          </Badge>
          <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl mb-6">
            Susunan Redaksi &amp; Standar Jurnalistik
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            ABADI.NEWS dikelola oleh tim profesional yang berdedikasi tinggi terhadap jurnalisme berkualitas. Kami berkomitmen menyajikan berita yang akurat, berimbang, dan independen untuk masyarakat.
          </p>
        </div>

        {}
        <div className="mx-auto max-w-4xl mb-20">
          <div className="grid gap-8 md:grid-cols-[1fr_2fr]">
            <div>
              <h2 className="font-default text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <Shield className="size-6 text-primary" />
                Standar Redaksi
              </h2>
              <p className="text-muted-foreground text-sm mb-6">
                Prinsip-prinsip utama yang menjadi landasan kerja seluruh awak redaksi ABADI.NEWS dalam memproduksi karya jurnalistik.
              </p>
            </div>
            
            <Accordion type="single" collapsible className="w-full" defaultValue="item-1">
              <AccordionItem value="item-1" className="border-border/50">
                <AccordionTrigger className="font-default text-base hover:text-primary">
                  <div className="flex items-center gap-3">
                    <Shield className="size-4 text-muted-foreground" />
                    Independensi Redaksi
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  Ruang redaksi ABADI.NEWS bekerja secara independen, bebas dari intervensi pihak manapun termasuk pemilik modal, pengiklan, maupun afiliasi politik. Keputusan editorial sepenuhnya berada di tangan Pemimpin Redaksi berdasarkan nilai berita dan kepentingan publik.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2" className="border-border/50">
                <AccordionTrigger className="font-default text-base hover:text-primary">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="size-4 text-muted-foreground" />
                    Proses Verifikasi &amp; Akurasi
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  Setiap informasi yang dipublikasikan harus melalui proses verifikasi berlapis (check and recheck). Kami mengutamakan akurasi di atas kecepatan. Jika terjadi kekeliruan, kami berkomitmen untuk segera melakukan ralat atau koreksi secara transparan sesuai pedoman media siber.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3" className="border-border/50">
                <AccordionTrigger className="font-default text-base hover:text-primary">
                  <div className="flex items-center gap-3">
                    <BookOpen className="size-4 text-muted-foreground" />
                    Pedoman Etik Jurnalistik
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  Seluruh jurnalis ABADI.NEWS wajib mematuhi Kode Etik Jurnalistik (KEJ) dan Pedoman Pemberitaan Media Siber. Kami menghormati hak privasi, asas praduga tak bersalah, dan menolak segala bentuk plagiarisme serta penyebaran berita bohong (hoaks).
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        <Separator className="my-16 opacity-50" />

        {}
        <div className="mb-10 flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div>
            <h2 className="font-default text-3xl font-bold text-foreground">Tim Redaksi</h2>
            <p className="mt-2 text-muted-foreground">Mengenal lebih dekat para penggerak di balik meja redaksi ABADI.NEWS.</p>
          </div>
        </div>

        <PostsProvider className="editorial-team-grid" wp_query={{
        post_type: "pengurus",
        per_page: 12,
        orderby: "title",
        order: "asc"
      }}>
          <EditorialTeamConsumer />
        </PostsProvider>

      </div>
    </section>;
}